import {
    Grid,
    useStyles,
    MemberTile,
    Flex,
    Button,
} from '@archipro-design/aria';
import type { UIMatch } from 'react-router';
import { useMatches, useSearchParams } from 'react-router';
import { useAppFetcher } from '~/hooks/use-app-fetcher';
import PageSection from '@modules/professional/component/page-layout/PageSection';
import * as S from './TeamMembers.style';

import React, { useState, useRef } from 'react';

import type { TeamMembersResult } from './TeamMembers';
import { getMemberPlaceholderImage } from './TeamMembers';
import type { TeamMemberView } from '@modules/professional/type';
import { ClientOnly } from 'remix-utils';
import { useMemoizedFn } from 'ahooks';
import {
    INFO_PAGE_MATCH_ID,
    INFO_PAGE_MATCH_ID_EDITMODE,
    PAGE_ANCHORS,
} from '@modules/professional/config/constants';
import { useTracker } from '@archipro-website/tracker';
import { useConfigContext } from '@archipro-website/config/bindings/react';

import { useScrollToHandle } from '@modules/root/hook/use-scrollto-handle';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import TeamMemberDetailsModalUpdated from './TeamMemberDetailsModalUpdated';
import { useProfessionalData } from '@modules/professional/hook/use-professional-data';
import type { APHandle } from '~/modules/root';
import type { ProfessionalTeamMemberDetailsLoaderData } from '~/modules/professional/page/TeamMemberDetailsPage';
import type { InfoPageLoaderData } from '~/modules/professional/api/info-page-loader.server';

export const TeamMembersDesktop: React.FC<
    TeamMembersResult & { sectionTitle: string }
> = ({ teamMembers, pageInfo, sectionTitle, disablePhotoViewer }) => {
    const anchorRef = useRef<HTMLDivElement>(null);
    const { css } = useStyles();
    const matches = useMatches();
    const routeMatch = matches[matches.length - 1] as UIMatch<
        ProfessionalTeamMemberDetailsLoaderData,
        APHandle
    >;

    const tracker = useTracker();
    const teamMemberFetcher = useAppFetcher<InfoPageLoaderData>();
    const [searchParams, setSearchParams] = useSearchParams();

    const isDialogRoute = routeMatch?.handle?.isDialogRoute;
    const memberProfileID = routeMatch?.data?.memberID;
    const hasNextPage =
        teamMemberFetcher?.data?.teamMembersResult?.pageInfo.hasNextPage ??
        teamMembers.length < pageInfo.totalCount;

    useScrollToHandle(PAGE_ANCHORS.TEAM_MEMBERS, anchorRef.current);

    const infoPagePath =
        matches.find((match) =>
            [INFO_PAGE_MATCH_ID, INFO_PAGE_MATCH_ID_EDITMODE].includes(
                match.id,
            ),
        )?.pathname ?? '/';

    const fetcherLoading = teamMemberFetcher.state === 'submitting';

    const loadMoreTeamMembers = () => {
        if (fetcherLoading) return;

        tracker.log('ProfessionalAllTeamClick', {
            url: new URL(window.location.href),
            targetTracker: 'archiproTracker',
        });

        setSearchParams(
            {
                ...searchParams,
                teamPage: ((pageInfo?.currentPage ?? 1) + 1).toString(),
            },
            {
                preventScrollReset: true,
            },
        );
    };

    return (
        <div
            className={css(hasNextPage ? S.TeamMemberWrapper : {})}
            ref={anchorRef}
        >
            <PageSection sectionTitle={sectionTitle}>
                <Grid columns={4} className={css(S.TeamMemberGrid)}>
                    {teamMembers.map((member) => (
                        <MemberDesktop
                            key={member.id}
                            member={member}
                            basePath={infoPagePath}
                            modalIsOpen={
                                isDialogRoute && member.id === memberProfileID
                            }
                            disablePhotoViewer={disablePhotoViewer}
                        />
                    ))}
                </Grid>
            </PageSection>

            {hasNextPage && (
                <Flex hAlign="start">
                    <Button
                        onClick={loadMoreTeamMembers}
                        size={16}
                        outlined={true}
                        className={css(S.LoadMoreButton)}
                    >
                        {!fetcherLoading ? `View more` : 'Loading...'}
                    </Button>
                </Flex>
            )}
        </div>
    );
};

const MemberDesktop: React.FC<{
    member: TeamMemberView;
    modalIsOpen?: boolean;
    disablePhotoViewer?: boolean;
    basePath: string;
}> = ({ member, modalIsOpen = false, disablePhotoViewer }) => {
    const { detailsPageImageSrc, name, jobTitle } = member;
    const [modalOpen, setModalOpen] = useState(modalIsOpen);
    const tracker = useTracker();
    const config = useConfigContext();
    const professionalId = useProfessionalData()?.professional.ID;

    const imageProps = {
        src: detailsPageImageSrc,
        width: 480,
        height: 560,
        unoptimized:
            detailsPageImageSrc ===
            getMemberPlaceholderImage(config.assetsBaseUrl)
                ? true
                : undefined,
    };

    const jobTitleProps = {
        maximumLines: 1,
        children: jobTitle ?? '',
    };

    const handleToggleDetails = useMemoizedFn(
        (event: React.SyntheticEvent<HTMLElement>) => {
            event.stopPropagation();
            if (!disablePhotoViewer) {
                setModalOpen(true);
            }
        },
    );

    return (
        <>
            <ClientOnly fallback={null}>
                {() => (
                    <TeamMemberDetailsModalUpdated
                        member={member}
                        website={member.website ?? ''}
                        toggleModal={() => {
                            setModalOpen(false);
                        }}
                        isOpen={modalOpen}
                        professionalId={professionalId}
                    />
                )}
            </ClientOnly>

            <MemberTile
                image={{
                    ...imageProps,
                    alt: name,
                    width: 480,
                    height: 560,
                    layout: 'fill',
                    sizes: getImagesSizes({
                        desktop: 480,
                        mobile: 174,
                    }),
                }}
                name={name}
                jobTitle={jobTitleProps}
                onToggleDetails={handleToggleDetails}
                onClick={() => {
                    tracker.log('ProfessionalExpandTeamClick', {
                        url: new URL(window.location.href),
                        targetTracker: 'archiproTracker',
                    });
                }}
            />
        </>
    );
};
